import React from "react";
import { Box, Typography, Button, CircularProgress, Alert } from "@mui/material";
import { useTheme } from "@mui/material/styles";

const LockIcon = ({ color = "#F59E0B" }) => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const LockOpenIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 9.9-1" />
  </svg>
);

/**
 * ScreenAccessOverlay
 * Renders a visual overlay when a screen is locked (openAccess === false).
 * Displays a centered card with lock icon, message, and an admin-only "Open Access" button.
 */
const ScreenAccessOverlay = ({
  isAdmin = false,
  onOpenAccess,
  isOpening = false,
  errorMessage = null,
  screenTitle = "Re-Invest for Q3",
}) => {
  const theme = useTheme();
  const t = theme.custom;
  const isDark = theme.palette.mode === "dark";

  const lockColor = isDark ? "#F59E0B" : "#D97706";

  return (
    <Box
      sx={{
        position: "fixed",
        top: "52px",
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: isDark ? "rgba(6, 14, 13, 0.8)" : "rgba(254, 255, 246, 0.85)",
        backdropFilter: "blur(3px)",
        p: { xs: 2, sm: 3 },
        overflowY: "auto",
      }}
    >
      <Box
        sx={{
          backgroundColor: t.surface,
          border: isDark ? "1px solid rgba(245, 158, 11, 0.4)" : "1px solid rgba(217, 119, 6, 0.4)",
          borderRadius: 3,
          boxShadow: isDark
            ? "0 25px 50px -12px rgba(0, 0, 0, 0.85), 0 0 25px rgba(245, 158, 11, 0.15)"
            : "0 20px 40px -12px rgba(3, 81, 77, 0.18), 0 0 25px rgba(217, 119, 6, 0.12)",
          p: { xs: 3.5, sm: 4.5 },
          maxWidth: 440,
          width: "100%",
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          m: "auto",
        }}
      >
        {/* Lock Icon Badge */}
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            backgroundColor: isDark ? "rgba(245, 158, 11, 0.12)" : "rgba(217, 119, 6, 0.12)",
            border: isDark ? "1px solid rgba(245, 158, 11, 0.3)" : "1px solid rgba(217, 119, 6, 0.3)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 2.5,
          }}
        >
          <LockIcon color={lockColor} />
        </Box>

        {/* Title */}
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            fontSize: { xs: "1.25rem", sm: "1.5rem" },
            color: t.text,
            mb: 1,
            letterSpacing: "-0.01em",
          }}
        >
          {screenTitle}
        </Typography>

        {/* Subtitle / Instructions */}
        <Typography
          sx={{
            color: t.text2,
            fontSize: "0.95rem",
            lineHeight: 1.6,
            maxWidth: 320,
            mb: isAdmin ? 3 : 0,
          }}
        >
          Please reach out to Investment League Admins for access.
        </Typography>

        {/* Error message alert (e.g. POST failure or GET retry failure) */}
        {errorMessage && (
          <Alert
            severity="error"
            sx={{
              width: "100%",
              mt: 2,
              mb: isAdmin ? 2.5 : 0,
              backgroundColor: isDark ? "rgba(239, 68, 68, 0.12)" : "rgba(239, 68, 68, 0.08)",
              color: isDark ? "#FCA5A5" : "#DC2626",
              border: isDark ? "1px solid rgba(239, 68, 68, 0.3)" : "1px solid rgba(239, 68, 68, 0.25)",
              fontSize: "0.85rem",
              textAlign: "left",
            }}
          >
            {errorMessage}
          </Alert>
        )}

        {/* Admin-only Open Access Button */}
        {isAdmin && (
          <Button
            variant="contained"
            onClick={onOpenAccess}
            disabled={isOpening}
            startIcon={
              isOpening ? (
                <CircularProgress size={16} sx={{ color: isDark ? "#0D1A18" : "#FFFFFF" }} />
              ) : (
                <LockOpenIcon />
              )
            }
            sx={{
              backgroundColor: isDark ? "#F59E0B" : "#D97706",
              color: isDark ? "#0D1A18" : "#FFFFFF",
              fontWeight: 600,
              fontSize: "0.9rem",
              textTransform: "none",
              px: 3.5,
              py: 1.1,
              borderRadius: 2,
              boxShadow: isDark ? "0 4px 14px rgba(245, 158, 11, 0.3)" : "0 4px 14px rgba(217, 119, 6, 0.25)",
              "&:hover": {
                backgroundColor: isDark ? "#D97706" : "#B45309",
                boxShadow: isDark ? "0 6px 20px rgba(245, 158, 11, 0.45)" : "0 6px 20px rgba(217, 119, 6, 0.35)",
              },
              "&.Mui-disabled": {
                backgroundColor: isDark ? "rgba(245, 158, 11, 0.4)" : "rgba(217, 119, 6, 0.4)",
                color: isDark ? "rgba(15, 23, 42, 0.6)" : "rgba(255, 255, 255, 0.7)",
              },
            }}
          >
            {isOpening ? "Opening Access..." : "Open Access"}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default ScreenAccessOverlay;

